// Add / edit form. Sheet with type picker for new entries.
//
// Save runs onSave inside the sheet — if it throws, the sheet stays open and
// the error is shown so the user can retry (this matters when the vault has
// auto-locked while the sheet was open).

import { el, clear } from './dom.js';
import { openSheet } from './sheet.js';
import { openTotpSetup } from './totp-setup.js';
import { openPasswordGenerator } from './password-generator.js';
import { normalizeBackupCodes } from '../util/backup-codes.js';
import { formatBytes } from '../util/format-bytes.js';

const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024; // 10 MB per file

const TYPES = [
  { type: 'login', label: 'Login' },
  { type: 'card', label: 'Card' },
  { type: 'license', label: 'Software License' },
  { type: 'identity', label: 'Identity' },
  { type: 'note', label: 'Secure Note' },
  { type: 'apiKey', label: 'API Key' },
];

export function openEntryForm({ entry, onSave, onCancel, autoLock }) {
  // Pause the auto-lock timer while the sheet is open so a long edit session
  // doesn't get killed by inactivity. Resumed via onDismiss on any close path.
  autoLock?.pause?.();

  let current = entry ? { ...entry } : null;
  const adds = [];                 // staged new files: { name, type, bytes:Uint8Array }
  const removals = new Set();       // ids of existing attachments to delete
  let attachError = '';             // last attach error (survives re-render)

  const main = el('div', { class: 'form-main' });
  const errorBox = el('div', { class: 'error form-error' });
  const footer = el('div', { class: 'form-actions' });
  const body = el('div', { class: 'form-shell' }, [main, errorBox, footer]);

  const sheetHandle = openSheet({
    kind: 'fullscreen',
    title: entry ? 'Edit entry' : 'New entry',
    body,
    onDismiss: () => { autoLock?.resume?.(); },
  });

  const close = () => sheetHandle.close();
  const cancel = () => { close(); onCancel?.(); };

  function showError(msg) {
    errorBox.textContent = msg;
  }

  function render() {
    showError('');
    clear(main);
    if (!current) {
      main.append(el('div', { class: 'form-grid' }, TYPES.map((t) =>
        el('button', { onclick: () => { current = blank(t.type); render(); } }, [t.label]),
      )));
      clear(footer);
      footer.append(el('button', { class: 'ghost', onclick: cancel }, ['Cancel']));
      return;
    }

    const fields = formFieldsFor(current);
    const grid = el('div', { class: 'form-grid' });
    for (const f of fields) grid.append(buildField(f, current, { render }));
    main.append(grid, attachmentsSection());

    clear(footer);
    const saveBtn = el('button', { class: 'primary', onclick: async () => {
      showError('');
      saveBtn.disabled = true;
      saveBtn.textContent = 'Saving…';
      try {
        await onSave(current, { adds, removals: [...removals] });
        close();
      } catch (err) {
        showError(err?.name === 'VaultLockedError'
          ? 'Safe is locked. Unlock it and try again.'
          : (err?.message ?? String(err)));
        saveBtn.disabled = false;
        saveBtn.textContent = 'Save';
      }
    } }, ['Save']);
    footer.append(
      el('button', { class: 'ghost', onclick: cancel }, ['Cancel']),
      saveBtn,
    );

    const firstInput = main.querySelector('input, textarea');
    setTimeout(() => firstInput?.focus(), 0);
  }

  function attachRow(name, size, onRemove) {
    return el('div', { class: 'attach-row' }, [
      el('span', { class: 'attach-name', title: name }, [name]),
      el('span', { class: 'attach-size' }, [formatBytes(size)]),
      el('button', { class: 'ghost attach-remove', type: 'button', onclick: onRemove }, ['Remove']),
    ]);
  }

  function attachmentsSection() {
    const list = el('div', { class: 'attach-list' });
    for (const a of (current.attachments ?? []).filter((a) => !removals.has(a.id))) {
      list.append(attachRow(a.name, a.size, () => { removals.add(a.id); render(); }));
    }
    adds.forEach((a, i) => {
      list.append(attachRow(a.name, a.bytes.byteLength, () => { adds.splice(i, 1); render(); }));
    });

    const fileInput = el('input', {
      type: 'file', multiple: true, style: { display: 'none' },
      onchange: async (e) => {
        attachError = '';
        const files = [...(e.target.files ?? [])];
        e.target.value = '';
        for (const f of files) {
          if (f.size > MAX_ATTACHMENT_BYTES) { attachError = `${f.name}: file too large (max 10 MB)`; continue; }
          try {
            const bytes = new Uint8Array(await f.arrayBuffer());
            adds.push({ name: f.name, type: f.type, bytes });
          } catch { attachError = `${f.name}: could not read file`; }
        }
        render();
      },
    });
    const addBtn = el('button', { class: 'ghost', type: 'button', onclick: () => fileInput.click() }, ['Attach file']);

    return el('div', { class: 'attachments-edit full' }, [
      el('div', { class: 'attachments-edit-label' }, ['Attachments']),
      list,
      addBtn,
      fileInput,
      el('div', { class: 'attach-error' }, [attachError]),
      el('div', { class: 'attach-note' }, ['Attachments are stored on this device only.']),
    ]);
  }

  render();
}

function blank(type) {
  switch (type) {
    case 'login': return { type, title: '', username: '', password: '', url: '', totp: '', backupCodes: '', notes: '' };
    case 'card': return { type, title: '', cardholder: '', number: '', expiryMonth: '', expiryYear: '', cvv: '', pin: '', notes: '' };
    case 'license': return { type, title: '', product: '', version: '', licenseKey: '', email: '', orderNumber: '', purchaseDate: '', notes: '' };
    case 'identity': return { type, title: '', firstName: '', lastName: '', email: '', phone: '', address: '', notes: '' };
    case 'note': return { type, title: '', body: '' };
    case 'apiKey': return { type, title: '', service: '', key: '', secret: '', notes: '' };
    default: return { type, title: '' };
  }
}

function formFieldsFor(e) {
  const common = [{ key: 'title', label: 'Title', full: true }];
  switch (e.type) {
    case 'login': return [...common,
      { key: 'username', label: 'Username' },
      { key: 'password', label: 'Password', secret: true, generate: true },
      { key: 'totp', custom: 'totp', full: true },
      { key: 'backupCodes', label: 'Recovery codes', textarea: true, full: true, placeholder: 'One code per line', normalize: normalizeBackupCodes },
      { key: 'url', label: 'URL', full: true },
      { key: 'notes', label: 'Notes', textarea: true, full: true },
    ];
    case 'card': return [...common,
      { key: 'cardholder', label: 'Cardholder' },
      { key: 'number', label: 'Number' },
      { key: 'expiryMonth', label: 'Expiry month' },
      { key: 'expiryYear', label: 'Expiry year' },
      { key: 'cvv', label: 'CVV', secret: true },
      { key: 'pin', label: 'PIN', secret: true },
      { key: 'notes', label: 'Notes', textarea: true, full: true },
    ];
    case 'license': return [...common,
      { key: 'product', label: 'Product' },
      { key: 'version', label: 'Version' },
      { key: 'licenseKey', label: 'License key', full: true, secret: true },
      { key: 'email', label: 'Email' },
      { key: 'orderNumber', label: 'Order number' },
      { key: 'purchaseDate', label: 'Purchase date' },
      { key: 'notes', label: 'Notes', textarea: true, full: true },
    ];
    case 'identity': return [...common,
      { key: 'firstName', label: 'First name' },
      { key: 'lastName', label: 'Last name' },
      { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Phone' },
      { key: 'address', label: 'Address', textarea: true, full: true },
      { key: 'notes', label: 'Notes', textarea: true, full: true },
    ];
    case 'note': return [...common,
      { key: 'body', label: 'Body', textarea: true, full: true },
    ];
    case 'apiKey': return [...common,
      { key: 'service', label: 'Service' },
      { key: 'key', label: 'Key', full: true, secret: true },
      { key: 'secret', label: 'Secret', full: true, secret: true },
      { key: 'notes', label: 'Notes', textarea: true, full: true },
    ];
    default: return common;
  }
}

function buildField(f, state, ctx) {
  if (f.custom === 'totp') return buildTotpField(state, ctx);
  // Keep every vault field away from browser/extension spellcheck. Enhanced
  // spell check (and Grammarly-class extensions) ship the contents of
  // type=text inputs and textareas to a remote service — and secret inputs
  // become type=text while focused for editing. That would send passwords,
  // TOTP seeds and note bodies off-device, which is the one thing this app
  // promises never happens.
  const noAssist = {
    spellcheck: false,
    autocorrect: 'off',
    autocapitalize: 'off',
    autocomplete: 'off',
    'data-1p-ignore': '',
  };
  const input = f.textarea
    ? el('textarea', {
        ...noAssist,
        value: state[f.key] ?? '',
        placeholder: f.placeholder,
        oninput: (e) => { state[f.key] = e.target.value; },
        // Optional paste-time normalization (e.g. backup codes → one per line).
        onpaste: f.normalize && ((e) => {
          const pasted = e.clipboardData?.getData('text') ?? '';
          e.preventDefault();
          const ta = e.target;
          const start = ta.selectionStart ?? ta.value.length;
          const end = ta.selectionEnd ?? ta.value.length;
          const insert = f.normalize(pasted);
          ta.value = ta.value.slice(0, start) + insert + ta.value.slice(end);
          const pos = start + insert.length;
          ta.selectionStart = ta.selectionEnd = pos;
          state[f.key] = ta.value;
        }),
      })
    : el('input', {
        ...noAssist,
        type: f.secret ? 'password' : 'text',
        value: state[f.key] ?? '',
        placeholder: f.placeholder,
        oninput: (e) => { state[f.key] = e.target.value; },
        // Secret fields reveal while focused (deliberate editing) and re-mask on blur.
        onfocus: f.secret ? (e) => { e.target.type = 'text'; } : undefined,
        onblur: f.secret ? (e) => { e.target.type = 'password'; } : undefined,
      });
  let control = input;
  if (f.generate) {
    const genBtn = el('button', {
      class: 'ghost pw-field-gen', type: 'button',
      onclick: () => openPasswordGenerator({
        onUse: (pw) => { state[f.key] = pw; ctx.render(); },
      }),
    }, ['Generate']);
    control = el('div', { class: 'pw-field-row' }, [input, genBtn]);
  }

  return el('label', { class: 'field' + (f.full ? ' full' : '') }, [
    el('span', {}, [f.label]),
    control,
  ]);
}

function buildTotpField(state, ctx) {
  const open = () => openTotpSetup({
    current: state,
    onPick: (s) => { state.totp = s; ctx.render(); },
  });

  const controls = state.totp
    ? [
        el('span', { class: 'totp-status' }, ['✓ One-time password configured']),
        el('button', { class: 'ghost', type: 'button', onclick: open }, ['Change']),
        el('button', { class: 'ghost', type: 'button', onclick: () => { state.totp = ''; ctx.render(); } }, ['Remove']),
      ]
    : [el('button', { class: 'ghost', type: 'button', onclick: open }, ['Setup Code…'])];

  return el('label', { class: 'field full totp-field' }, [
    el('span', {}, ['One-time password']),
    el('div', { class: 'totp-controls' }, controls),
  ]);
}
